import React, { createContext, useContext, useEffect, useState } from "react";

const ResponsiveContext = createContext();

export const useResponsive = () => {
  const context = useContext(ResponsiveContext);
  if (!context) {
    throw new Error("useResponsive must be used within a ResponsiveProvider");
  }
  return context;
};

export const ResponsiveProvider = ({ children }) => {
  const [isVeryWideScreen, setIsVeryWideScreen] = useState(false);
  const [contentMaxWidth, setContentMaxWidth] = useState("100%");
  const [buttonContainerWidth, setButtonContainerWidth] = useState("96%");
  const [shouldHideButtons, setShouldHideButtons] = useState(false);

  useEffect(() => {
    const updateResponsiveState = () => {
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const aspectRatio = viewportWidth / viewportHeight;

      // Don't apply responsive width constraints on mobile devices
      if (viewportWidth <= 768) {
        setIsVeryWideScreen(false);
        setContentMaxWidth("100%");
        setButtonContainerWidth("100%");
        setShouldHideButtons(true);
        return;
      }

      const veryWideAspectRatio = 2.2; // Very wide
      const veryLowHeight = 500; // Hide buttons when height is below this (increased from 400)

      if (aspectRatio >= veryWideAspectRatio) {
        // Very wide screens: constrain content width
        // The content sections have 96% width, so we need to account for that
        // Button container: 70% of viewport (increased from 60%)
        // Content container: needs to be sized so that 96% of it equals button width
        const buttonPercent = 70;
        const contentPercent = buttonPercent / 0.96; // Compensate for 96% sections

        setIsVeryWideScreen(true);
        setContentMaxWidth(`${contentPercent}%`);
        setButtonContainerWidth(`${buttonPercent}%`);

        // Hide buttons if screen is very wide AND very low height
        setShouldHideButtons(viewportHeight < veryLowHeight);
      } else {
        // Normal screens: full width
        setIsVeryWideScreen(false);
        setContentMaxWidth("100%");
        setButtonContainerWidth("100%");
        setShouldHideButtons(false);
      }
    };

    // Initial calculation
    updateResponsiveState();

    // Add resize listener
    window.addEventListener("resize", updateResponsiveState);
    return () => window.removeEventListener("resize", updateResponsiveState);
  }, []);

  return (
    <ResponsiveContext.Provider
      value={{
        isVeryWideScreen,
        contentMaxWidth,
        buttonContainerWidth,
        shouldHideButtons,
      }}
    >
      {children}
    </ResponsiveContext.Provider>
  );
};
